import { useCallback, useEffect, useRef, useState } from "react";
import { downloadFile } from "../utils/helpers";
import { exportConfigFile } from "../issuance/configFile";
import {
  sanitizeFilename,
  generateTimestamp,
} from "../../shared/utils/fileHelpers";
import {
  CONFIG_FILE_EXTENSION,
  CONFIG_FILE_MIME,
} from "../../shared/issuance/config";
import {
  apiGetConfiguration,
  ApiError,
  SHORT_CALL_TIMEOUT_MS,
  withTimeoutSignal,
} from "../issuance/apiClient";

/**
 * ONE implementation of "fetch the Configuration File and hand it to
 * the customer", shared by every screen that offers it.
 *
 * WHY A HOOK : Step 3's step-away line and Step 4's signing-wait line
 * are the same promise made at two moments, and they were briefly two
 * hand-written copies - identical timeout wording, identical null
 * guard, identical filename shape, identical disabled treatment. That
 * is the drift shape this file's sibling hook (useCopyFeedback) and
 * the poll-cooldown notice in Step4Download were both extracted to
 * prevent : the first change to the timeout message or the error
 * wording would have landed on one screen and not the other, on the
 * one control that saves a customer's progress.
 *
 * The ISSUED screen deliberately does NOT use this : its download
 * merges the issued certificate into the file and returns early
 * without a bundle, which is a different operation with a different
 * precondition.
 *
 * @param {string} sessionId      - the session to fetch the file for
 * @param {string} primaryDomain  - drives the download filename
 * @returns {{saving: boolean, error: string|null, clearError: function, save: function}}
 */
export function useConfigFileSave(sessionId, primaryDomain) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Mounted guard - the pattern every async-setState site in the
  // Issuance Tool follows. save() awaits a network round-trip and then
  // writes state in a finally block ; a customer who navigates away
  // mid-flight must not trigger a setState on an unmounted component.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      // Bounded fetch : this handler holds `saving` true across the
      // await, so an unbounded hang would leave the control disabled
      // PERMANENTLY - on the one control that saves the customer's
      // progress.
      const data = await withTimeoutSignal(SHORT_CALL_TIMEOUT_MS, (signal) =>
        apiGetConfiguration(sessionId, { signal })
      );
      if (!mountedRef.current) return;
      // The apiClient `call` helper resolves an off-contract
      // 200-with-empty-body to null. Passing that straight to
      // exportConfigFile would throw a raw lowercase TypeError into
      // the customer's Alert, so raise the house-style sentence
      // first.
      if (!data) {
        throw new Error("The server returned an empty configuration file.");
      }
      const text = exportConfigFile(data);
      const filename =
        sanitizeFilename(primaryDomain || "trustico") +
        "-" +
        generateTimestamp() +
        "." +
        CONFIG_FILE_EXTENSION;
      downloadFile(text, filename, CONFIG_FILE_MIME);
    } catch (e) {
      if (!mountedRef.current) return;
      setError(
        e && e.name === "AbortError"
          ? "The configuration file request timed out. Please try again."
          : e instanceof ApiError
            ? e.message
            : "The configuration file could not be built. " +
              (e.message || "Unknown error.")
      );
    } finally {
      if (mountedRef.current) {
        setSaving(false);
      }
    }
  }, [sessionId, primaryDomain]);

  return { saving, error, clearError, save };
}

export default useConfigFileSave;
